import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MusicOffIcon from '@mui/icons-material/MusicOff';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Board, { InteractionMode } from '../components/Board.component';
import SetupScreen, { SimConfig } from '../components/SetupScreen.component';
import Controls from '../components/Controls.component';
import PopulationGraph from '../components/PopulationGraph.component';
import { useSimulation } from '../hooks/useSimulation';

const formatTime = (ms: number) => {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const Home = () => {
  const [simStarted, setSimStarted] = useState(false);
  const [config, setConfig] = useState<SimConfig>({
    fishCounts: [50, 50, 0],
    predatorCount: 0,
    mapWidth: 1000,
    mapHeight: 1000,
    walls: [],
  });
  const [mode, setMode] = useState<InteractionMode>('none');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const elapsedAtPauseRef = useRef(0);
  const lastResumeRef = useRef(0);

  useEffect(() => {
    if (!audioRef.current) {
      const audio = new Audio('/song.mp3');
      audio.loop = true;
      audioRef.current = audio;
    }
    const audio = audioRef.current;
    if (simStarted && soundEnabled && !isPaused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [simStarted, soundEnabled, isPaused]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Timer: accumulates elapsed sim time, stops while paused
  useEffect(() => {
    if (!simStarted) return;

    if (isPaused) {
      elapsedAtPauseRef.current += Date.now() - lastResumeRef.current;
      return;
    }

    lastResumeRef.current = Date.now();
    const id = setInterval(() => {
      setElapsedMs(elapsedAtPauseRef.current + (Date.now() - lastResumeRef.current));
    }, 200);
    return () => clearInterval(id);
  }, [simStarted, isPaused]);

  const { stateRef, postFish, postFood, postPredator, postConfig, postWall, postSpeed, postErase, postPause } = useSimulation();

  const handleStart = useCallback(
    async (cfg: SimConfig) => {
      setConfig(cfg);
      await postConfig({
        fishCounts: cfg.fishCounts,
        predatorCount: cfg.predatorCount,
        mapWidth: cfg.mapWidth,
        mapHeight: cfg.mapHeight,
        walls: cfg.walls,
      });
      setSimStarted(true);
    },
    [postConfig]
  );

  const handleReset = useCallback(() => {
    setSimStarted(false);
    setMode('none');
    setSoundEnabled(true);
    setIsPaused(false);
    setElapsedMs(0);
    elapsedAtPauseRef.current = 0;
    lastResumeRef.current = 0;
  }, []);

  const handlePause = useCallback(async () => {
    setIsPaused(prev => !prev);
    await postPause();
  }, [postPause]);

  const handleAddFish = useCallback(
    (x: number, y: number, flock: number) => postFish(x, y, flock),
    [postFish]
  );

  if (!simStarted) {
    return <SetupScreen onStart={handleStart} />;
  }

  return (
    <Box sx={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#0a1628' }}>
      {/* Header */}
      <Box sx={{
        px: 3, py: 0.75, display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: '#0d1f35', borderBottom: '1px solid #1e3a5f', flexShrink: 0,
      }}>
        <Typography variant="h6" sx={{ color: '#00e5ff', fontFamily: 'monospace', letterSpacing: 2, fontSize: 16 }}>
          BOIDS
        </Typography>

        <Typography sx={{ color: '#90a4ae', fontFamily: 'monospace', fontSize: 14, letterSpacing: 1, minWidth: 52 }}>
          {formatTime(elapsedMs)}
        </Typography>

        <Tooltip title={isPaused ? 'Resume simulation' : 'Pause simulation'} placement="bottom">
          <IconButton
            onClick={handlePause}
            size="small"
            sx={{
              color: isPaused ? '#ff9800' : '#00e5ff',
              border: `1px solid ${isPaused ? '#ff980044' : '#00e5ff44'}`,
              borderRadius: 1,
              '&:hover': { bgcolor: isPaused ? '#ff980022' : '#00e5ff22' },
            }}
          >
            {isPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1 }} />
        <a href="https://www.buymeacoffee.com/josueagbekodo" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
          <img src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" alt="Buy Me A Coffee" style={{ height: 32 }} />
        </a>
        <Tooltip title={soundEnabled ? 'Pause music' : 'Play music'} placement="bottom">
          <IconButton
            onClick={() => setSoundEnabled(s => !s)}
            size="small"
            sx={{
              color: soundEnabled ? '#00e5ff' : '#607d8b',
              border: `1px solid ${soundEnabled ? '#00e5ff44' : '#607d8b44'}`,
              borderRadius: 1,
              '&:hover': { bgcolor: soundEnabled ? '#00e5ff22' : '#607d8b22' },
            }}
          >
            {soundEnabled ? <MusicNoteIcon fontSize="small" /> : <MusicOffIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Simulation canvas */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <Board
            stateRef={stateRef}
            mapWidth={config.mapWidth}
            mapHeight={config.mapHeight}
            mode={mode}
            onAddFish={handleAddFish}
            onAddFood={postFood}
            onAddPredator={postPredator}
            onAddWall={postWall}
            onErase={postErase}
          />
          <Controls
            stateRef={stateRef}
            mode={mode}
            onModeChange={setMode}
            onSpeedChange={postSpeed}
            onReset={handleReset}
          />
        </Box>

        {/* Population graph */}
        <Box sx={{ height: 150, flexShrink: 0, bgcolor: '#0d1f35', borderTop: '1px solid #1e3a5f' }}>
          <PopulationGraph stateRef={stateRef} isPaused={isPaused} />
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
